import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { useAuth } from '@/composables/useAuth';

// Determine client from server config, Vite env, or hostname
function getClientFromHostname(): string {
  // Prefer server-injected CLIENT_ROUTE (production) or Vite env (development)
  const configuredRoute = window.__SERVER_CONFIG__?.clientRoute || import.meta.env.VITE_CLIENT_ROUTE;
  if (configuredRoute) {
    return configuredRoute;
  }

  const hostname = window.location.hostname;
  
  // Exact matches (for local development)
  if (hostname === 'localhost') {
    return 'classify';
  }
  
  // Prefix-based matches
  if (hostname.startsWith('uscensus.') || hostname.startsWith('uscensus-')) {
    return 'uscensus';
  }
  
  return 'classify';
}

// Get the home component based on client
function getHomeComponent() {
  const client = getClientFromHostname();
  if (client === 'uscensus') {
    return () => import('@/views/USCensusView.vue');
  }
  return () => import('@/views/HomeView.vue');
}

// GHY keeps its legacy full-classifier proxy page; every other client uses ProxyView.
function getProxyComponent() {
  if (getClientFromHostname() === 'ghy') {
    return () => import('@/views/GhyProxyView.vue');
  }
  return () => import('@/views/ProxyView.vue');
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: getHomeComponent(),
    meta: { requiresAuth: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/help',
    name: 'help',
    component: () => import('@/views/HelpView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/help-duty',
    name: 'help-duty',
    component: () => import('@/views/HelpDutyView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/proxy',
    name: 'proxy',
    component: getProxyComponent(),
    meta: { requiresAuth: false },
  },
  {
    path: '/bsgov',
    name: 'bsgov',
    component: () => import('@/views/BsgovView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/ecas',
    name: 'ecas',
    component: () => import('@/views/EcasView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/besw-embed',
    name: 'besw-embed',
    component: () => import('@/views/BahamasEmbedView.vue'),
    meta: { requiresAuth: false, profileKey: 'besw' },
  },
  {
    path: '/bsgov-embed',
    name: 'bsgov-embed',
    component: () => import('@/views/BahamasEmbedView.vue'),
    meta: { requiresAuth: false, profileKey: 'bsgov' },
  },
  {
    path: '/ecas-embed',
    name: 'ecas-embed',
    component: () => import('@/views/BahamasEmbedView.vue'),
    meta: { requiresAuth: false, profileKey: 'ecas' },
  },
  {
    path: '/mobile',
    name: 'mobile',
    component: () => import('@/views/MobileView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
  },
];

function getSpaBase(): string {
  const configured = window.__SERVER_CONFIG__?.spaBase;
  if (typeof configured === 'string' && configured.length > 0) {
    return configured.endsWith('/') ? configured : `${configured}/`;
  }
  const client = window.__SERVER_CONFIG__?.clientRoute || import.meta.env.VITE_CLIENT_ROUTE;
  if (client === 'bahamas') {
    return '/';
  }
  return '/ui/';
}

export const router = createRouter({
  history: createWebHistory(getSpaBase()),
  routes,
});

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const { authEnabled, bootstrap, isAuthenticated, login } = useAuth();

  // When IAM login is not enabled (uscensus, tjxeproxy, local without config),
  // preserve the historical allow-all behavior.
  if (!authEnabled) {
    next();
    return;
  }

  // Make sure we've called /ui/auth/me at least once before deciding.
  await bootstrap();

  const requiresAuth = to.meta?.requiresAuth !== false;
  if (requiresAuth && !isAuthenticated.value) {
    // Full-page redirect through the backend so the PKCE/state lives in the
    // express-session. Carry the requested path forward so the user lands
    // where they meant to after callback.
    login(to.fullPath);
    next(false);
    return;
  }

  next();
});

export default router;

